import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@/environments/environment';
import { SpdfService } from '@/app/services/ia-services/spdf.service';
import { ProjectsService } from '@/app/services/ia-services/projects.service';
import { SendSpdfReportPayload, SpdfReportContext } from '@/app/interfaces/report-spdf';
import { StoredUserData } from '@/app/interfaces/projects-list';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { ToastService } from '@/app/services/script/toast.service';
import { NgxExtendedPdfViewerModule, PdfSidebarView } from 'ngx-extended-pdf-viewer';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-report-viewer',
  standalone: true,
  imports: [CommonModule, ButtonsComponent, NgxExtendedPdfViewerModule, ConfirmDialog],
  templateUrl: './report-viewer.component.html',
  styleUrl: './report-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class ReportViewerComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly spdfService = inject(SpdfService);
  private readonly projectsService = inject(ProjectsService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly generationIdParamName = 'generationId';
  private readonly applicationId = environment.appId;
  private readonly userData: StoredUserData = JSON.parse(localStorage.getItem('userData') ?? '{}') as StoredUserData;
  private isDestroyed = false;
  private hasRequestedPdf = false;

  protected readonly documentName = 'id_24d23e4-1c2b';
  protected readonly thumbnailSidebarView = PdfSidebarView.THUMBS;
  protected readonly generationId = signal<string | null>(null);
  protected readonly reportContext = signal<SpdfReportContext | null>(null);
  protected readonly hasError = signal(false);
  protected readonly sendStatus = signal<'ready' | 'sending'>('ready');
  protected readonly canSend = computed(
    () =>
      !!this.pdfUrl() &&
      !!this.generationId() &&
      !!this.reportContext() &&
      this.sendStatus() === 'ready' &&
      this.canCurrentUserSendReport()
  );
  protected readonly pdfUrl = signal<string | null>(null);

  ngOnInit(): void {
    void this.loadPdf();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.revokePdfUrl();
  }

  protected sendReport(): void {
    this.confirmationService.confirm({
      message: 'Esta acción enviará el informe de forma definitiva. ¿Desea continuar?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Enviar',
      },
      accept: () => {
        this.confirmSendReport();
      },
    });
  }

  protected async goBackToProjectsList(): Promise<void> {
    await this.router.navigate(['/catalogo-ia/proyectos/listado-proyectos']);
  }

  private async confirmSendReport(): Promise<void> {
    const generationId = this.generationId();
    const context = this.reportContext();

    if (!generationId || !context) {
      return;
    }

    const payload: SendSpdfReportPayload = {
      idProyecto: context.idProyecto,
      nombreProyecto: context.nombreProyecto,
      userRole: 'JS',
      state: 'APPROVED',
      s3Key: context.s3Key,
    };

    this.sendStatus.set('sending');

    try {
      await this.projectsService.sendGeneratedReport(payload);
      sessionStorage.removeItem(this.getSpdfReportContextStorageKey(generationId));
      this.reportContext.set(null);
      await this.goBackToProjectsList();
    } catch {
      return;
    } finally {
      this.sendStatus.set('ready');
    }
  }

  private async loadPdf(): Promise<void> {
    if (this.hasRequestedPdf) {
      return;
    }

    const generationId = this.route.snapshot.queryParamMap.get(this.generationIdParamName);

    this.generationId.set(generationId);

    if (!generationId) {
      this.hasError.set(true);
      return;
    }

    const context = this.readReportContext(generationId);

    if (!context) {
      this.hasError.set(true);
      return;
    }

    this.reportContext.set(context);
    this.hasRequestedPdf = true;

    try {
      const pdfBlob = await this.spdfService.getPdf(generationId, this.applicationId);

      if (this.isDestroyed) {
        return;
      }

      if (pdfBlob.type !== 'application/pdf') {
        throw new Error('La respuesta de SPDF no es un PDF valido');
      }

      this.setPdfUrl(URL.createObjectURL(pdfBlob));

      try {
        await this.uploadPdf(context.uploadUrl, pdfBlob);
      } catch {
        this.toastService.showError(
          'Error al subir el informe',
          'El PDF se ha generado, pero no se ha podido persistir en S3'
        );
      }
    } catch {
      if (!this.isDestroyed) {
        this.hasError.set(true);
      }
    }
  }

  private async uploadPdf(uploadUrl: string, pdfBlob: Blob): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/pdf',
      },
      body: pdfBlob,
    });

    if (!response.ok) {
      throw new Error('No se ha podido subir el PDF a S3');
    }
  }

  private setPdfUrl(pdfUrl: string): void {
    this.revokePdfUrl();
    this.pdfUrl.set(pdfUrl);
  }

  private revokePdfUrl(): void {
    const pdfUrl = this.pdfUrl();

    if (!pdfUrl) {
      return;
    }

    URL.revokeObjectURL(pdfUrl);
    this.pdfUrl.set(null);
  }

  private readReportContext(generationId: string): SpdfReportContext | null {
    const rawContext = sessionStorage.getItem(this.getSpdfReportContextStorageKey(generationId));

    if (!rawContext) {
      return null;
    }

    try {
      const context = JSON.parse(rawContext) as Partial<SpdfReportContext>;

      if (typeof context.idProyecto !== 'number' || !Number.isFinite(context.idProyecto)) {
        return null;
      }

      if (typeof context.nombreProyecto !== 'string' || !context.nombreProyecto.trim()) {
        return null;
      }

      if (typeof context.codigo_organismo !== 'string' || !context.codigo_organismo.trim()) {
        return null;
      }

      if (typeof context.uploadUrl !== 'string' || !context.uploadUrl.trim()) {
        return null;
      }

      if (typeof context.s3Key !== 'string' || !context.s3Key.trim()) {
        return null;
      }

      return {
        idProyecto: context.idProyecto,
        nombreProyecto: context.nombreProyecto,
        codigo_organismo: context.codigo_organismo,
        uploadUrl: context.uploadUrl,
        s3Key: context.s3Key,
      };
    } catch {
      return null;
    }
  }

  private canCurrentUserSendReport(): boolean {
    const projectOrgCode = this.reportContext()?.codigo_organismo;

    if (!projectOrgCode) {
      return false;
    }

    return (
      this.userData.roles?.jefe_servicio_OGIA?.some((userOrg) => this.isSameOrgCode(userOrg, projectOrgCode)) ?? false
    );
  }

  private normalizeOrgCode(orgCode: string): string {
    const normalizedOrgCode = orgCode.match(/ORG\d+$/);

    return normalizedOrgCode ? normalizedOrgCode[0] : orgCode;
  }

  private isSameOrgCode(leftOrgCode: string, rightOrgCode: string): boolean {
    return this.normalizeOrgCode(leftOrgCode) === this.normalizeOrgCode(rightOrgCode);
  }

  private getSpdfReportContextStorageKey(generationId: string): string {
    return `spdf-report-context:${generationId}`;
  }
}
